import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ServerErrorPageProps {
  error?: {
    status?: number;
    message?: string;
    detail?: string;
    endpoint?: string;
  };
  onRetry?: () => void;
  onGoHome?: () => void;
}

export function ServerErrorPage({
  error,
  onRetry,
  onGoHome,
}: ServerErrorPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      navigate('/dashboard');
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const getErrorTitle = () => {
    if (error?.status === 500) {
      return t('errors.serverError', 'Internal Server Error');
    }
    if (error?.status === 503) {
      return t('errors.serviceUnavailable', 'Service Unavailable');
    }
    if (error?.status === 504) {
      return t('errors.gatewayTimeout', 'Gateway Timeout');
    }
    return t('errors.somethingWentWrong', 'Something Went Wrong');
  };

  const getErrorDescription = () => {
    if (error?.detail) {
      return error.detail;
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.status === 500) {
      return t(
        'errors.serverErrorDescription',
        'The server encountered an unexpected condition that prevented it from fulfilling the request.'
      );
    }
    if (error?.status === 503) {
      return t(
        'errors.serviceUnavailableDescription',
        'The server is temporarily unable to handle the request. Please try again later.'
      );
    }
    if (error?.status === 504) {
      return t(
        'errors.gatewayTimeoutDescription',
        'The server took too long to respond. Please try again.'
      );
    }
    return t(
      'errors.defaultDescription',
      'An unexpected error occurred. Please try again or contact support if the problem persists.'
    );
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <Card className="max-w-2xl w-full shadow-lg">
        <CardContent className="p-8 sm:p-12">
          <div className="text-center">
            {/* Error Icon */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 sm:w-14 sm:h-14 text-red-600" />
            </div>

            {/* Error Code */}
            {error?.status && (
              <div className="mb-4">
                <span className="inline-block px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-mono font-semibold">
                  {error.status}
                </span>
              </div>
            )}

            {/* Error Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {getErrorTitle()}
            </h1>

            {/* Error Description */}
            <p className="text-gray-600 mb-6 text-base sm:text-lg max-w-md mx-auto">
              {getErrorDescription()}
            </p>

            {/* Technical Details (if available) */}
            {error?.endpoint && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 mb-2">
                  {t('errors.technicalDetails', 'Technical Details')}
                </summary>
                <div className="bg-gray-50 p-4 rounded-lg text-left">
                  <p className="text-xs font-mono text-gray-600 break-all">
                    <span className="font-semibold">
                      {t('errors.endpoint', 'Endpoint')}:
                    </span>{' '}
                    {error.endpoint}
                  </p>
                  {error.message && (
                    <p className="text-xs font-mono text-gray-600 mt-2">
                      <span className="font-semibold">
                        {t('errors.message', 'Message')}:
                      </span>{' '}
                      {error.message}
                    </p>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={handleRetry}
                size="lg"
                className="w-full sm:w-auto"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.retry', 'Try Again')}
              </Button>

              <Button
                onClick={handleGoHome}
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
              >
                <Home className="w-4 h-4 mr-2" />
                {t('common.goHome', 'Go to Home')}
              </Button>
            </div>

            {/* Help Text */}
            <p className="mt-8 text-sm text-gray-500">
              {t(
                'errors.helpText',
                'If this problem persists, please contact support.'
              )}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
